import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Store, Megaphone, MapPin, CheckCircle2, DollarSign, Shield, ArrowRight, Route } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1501610862033-8787c0add568?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxjeWNsaW5nJTIwcm9hZCUyMHN1bnNldCUyMHJpZGVyfGVufDB8fHx8MTc4ODg4NTgwMXww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="min-h-screen">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-white rounded-lg px-2.5 py-1.5 flex items-center">
              <img src="/velopaths-logo.png" alt="Velopaths" className="h-6 w-auto" />
            </div>
            <span className="text-[10px] uppercase tracking-widest text-primary font-mono mt-1">Partner</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/explore"><Button variant="ghost" data-testid="nav-explore-button"><MapPin className="w-4 h-4 mr-1" />Explore paths</Button></Link>
            <Link to="/login"><Button variant="ghost" data-testid="nav-login-button">Sign in</Button></Link>
            <Link to="/signup/shop"><Button data-testid="nav-getstarted-button">Get started <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-16 min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="cyclist" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20 mb-6 font-mono uppercase tracking-widest">
              Multi-Tenant Partner Platform
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Power the routes riders <span className="gradient-text">love to ride.</span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground mt-6 max-w-xl leading-relaxed">
              Bike shops, rental operators, tour companies and local cycling partners manage sponsored pins, curated attractions, categories and
              subscription billing — all the content that fuels the Velopaths Rider App.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link to="/signup/shop">
                <Button size="lg" className="w-full sm:w-auto" data-testid="hero-shop-signup">
                  <Store className="w-4 h-4 mr-2" /> Become a Partner
                </Button>
              </Link>
              <Link to="/signup/advertiser">
                <Button size="lg" variant="outline" className="w-full sm:w-auto" data-testid="hero-advertiser-signup">
                  <Megaphone className="w-4 h-4 mr-2" /> Advertise a business
                </Button>
              </Link>
            </div>
            <div className="mt-5">
              <Link to="/explore" data-testid="hero-explore-link" className="group inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all">
                <MapPin className="w-4 h-4" /> Just want to ride? Explore bike paths near you
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Store, title: "Partners", desc: "Business and Trail Partners approve advertiser listings, create unpaid attractions, define custom categories and track revenue-share payouts.", color: "text-primary" },
            { icon: Megaphone, title: "Advertisers", desc: "Guided onboarding, live pin preview, $99/year Founding Advertiser billing and partner approval before going live.", color: "text-orange-400" },
            { icon: Shield, title: "Velopaths Admin", desc: "Global oversight of every tenant, revenue-share percentage control and full payout logs.", color: "text-cyan-400" },
          ].map((c) => (
            <div key={c.title} className="p-6 rounded-2xl border border-border bg-card hover:border-primary/40 transition-colors">
              <c.icon className={`w-8 h-8 ${c.color} mb-4`} />
              <h3 className="font-heading text-xl font-bold mb-2">{c.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Who can become a partner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-6" data-testid="who-can-partner">
        <div className="rounded-2xl border border-border bg-card p-8 sm:p-10">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-2">Who Can Become a Partner?</h2>
          <p className="text-sm text-muted-foreground mb-8 max-w-2xl">You don't need to own a bike shop to become a Velopaths Partner. If you know and love your local trails, there's a place for you.</p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-6">
              <div className="flex items-center gap-2 mb-4"><Store className="w-5 h-5 text-blue-400" /><h3 className="font-heading text-lg font-bold">Business Partners</h3></div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {["Bike Shops", "Rental Operators", "Tour Companies"].map((x) => (
                  <li key={x} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0" />{x}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-6">
              <div className="flex items-center gap-2 mb-4"><Route className="w-5 h-5 text-emerald-400" /><h3 className="font-heading text-lg font-bold">Trail Partners</h3></div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {["Local Cyclists", "Cycling Clubs", "Trail Advocates", "Nonprofits", "Tourism Organizations"].map((x) => (
                  <li key={x} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />{x}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-8">
            <Link to="/signup/shop"><Button data-testid="who-can-partner-apply" className="rounded-full">
              <Store className="w-4 h-4 mr-2" /> Apply as a Partner
            </Button></Link>
          </div>
        </div>
      </section>

      {/* Features strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: CheckCircle2, t: "Approval workflow" },
            { icon: MapPin, t: "Shop attractions" },
            { icon: DollarSign, t: "Revenue-share payouts" },
            { icon: Shield, t: "Full tenant isolation" },
          ].map((f) => (
            <div key={f.t} className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card">
              <f.icon className="w-5 h-5 text-primary shrink-0" />
              <span className="text-sm font-medium">{f.t}</span>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-border py-8 text-center text-sm text-muted-foreground">
        Velopaths Partner App — MVP. Rider App sold separately.
        <div className="mt-2">
          Support: <a href="mailto:info@velopaths.com" className="text-primary hover:underline">info@velopaths.com</a>
        </div>
      </footer>
    </div>
  );
}
